// Подписка на real-time канал Socket.IO + автоматическая invalidation
// конкретных TanStack Query keys.
//
// Этот хук — backward-compat обёртка над useSocketEvent для существующих
// consumer'ов (essays-realtime-sync, notification-list, streak-modal,
// material-workspace, routes/materials, routes/flashcards). Новый код пишем
// через `useSocketEvent` напрямую (типизированный payload).
//
// `channel` принимает строку (без autocomplete по типу) для совместимости
// с динамическими каналами вроде `material:${id}` — но фактическая подписка
// идёт на тот же event-name (Socket.IO server emit'ит в room, имя event'а
// остаётся фиксированным; channel-by-id фильтр делается на сервере через
// emitToUser/emitToRoom).

import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useSocket } from "@/providers/socket-provider";

interface UseRealtimeOptions {
	/**
	 * Имя Socket.IO event для подписки.
	 * Принимаем generic string ради backward-compat с динамическими каналами
	 * `material:${id}` (исторический контракт socket-mock). Реальные event'ы
	 * сервера — см. ServerToClientEvents.
	 */
	channel: string;
	/** Какие query keys инвалидировать при наступлении события. */
	invalidateQueryKeys?: ReadonlyArray<ReadonlyArray<unknown>>;
	/** Доп. колбэк (опционально). Payload приходит как unknown — узкое типирование через useSocketEvent. */
	onEvent?: (payload: unknown) => void;
}

export function useRealtime({
	channel,
	invalidateQueryKeys = [],
	onEvent,
}: UseRealtimeOptions): void {
	const qc = useQueryClient();
	const socket = useSocket();
	useEffect(() => {
		if (!socket) return;
		const listener = (payload: unknown): void => {
			onEvent?.(payload);
			for (const key of invalidateQueryKeys) {
				void qc.invalidateQueries({ queryKey: key });
			}
		};
		// channel — generic string; socket.io принимает любые event-names.
		socket.on(channel as never, listener as never);
		return () => {
			socket.off(channel as never, listener as never);
		};
	}, [socket, channel, qc, invalidateQueryKeys, onEvent]);
}
